import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { Product2AttributeSchema } from "../product2/product2-attribute-types";
import {
  Product2ImageCreateSchema,
  Product2ImageSchema,
} from "../product2/product2-types";

/* CatalogVariantComponent */
const CatalogVariantComponentSchema_ = z.object({
  _id: zExt.objectId(),
  variant: zExt.objectId(),
  quantity: z.number(),
});
export const CatalogVariantComponentSchema: z.ZodType<CatalogVariantComponent> =
  CatalogVariantComponentSchema_;
export interface CatalogVariantComponent
  extends z.infer<typeof CatalogVariantComponentSchema_> {}

const CatalogVariantComponentCreateSchema_ =
  CatalogVariantComponentSchema_.omit({ _id: true });
export const CatalogVariantComponentCreateSchema: z.ZodType<CatalogVariantComponentCreate> =
  CatalogVariantComponentCreateSchema_;
export type CatalogVariantComponentCreate = z.infer<
  typeof CatalogVariantComponentCreateSchema_
>;

/* CatalogVariant */
export const CatalogVariantSchema_ = z.object({
  _id: zExt.objectId(),
  attributeValues: z.array(zExt.objectId()),
  images: z.array(zExt.objectId()),
  components: z.array(CatalogVariantComponentSchema),
  price: z.number().nullish(),
  msrp: z.number().nullish(),
  barcode: z.string().nullish(),
  minQuantity: z.number().min(0).default(0),
});
export const CatalogVariantSchema: z.ZodType<CatalogVariant> =
  CatalogVariantSchema_;
export interface CatalogVariant extends z.infer<typeof CatalogVariantSchema_> {}

const CatalogVariantCreateSchema_ = CatalogVariantSchema_.omit({
  _id: true,
}).extend({ components: z.array(CatalogVariantComponentCreateSchema) });
export const CatalogVariantCreateSchema: z.ZodType<CatalogVariantCreate> =
  CatalogVariantCreateSchema_;
export type CatalogVariantCreate = z.infer<typeof CatalogVariantCreateSchema_>;

/* CatalogProduct */
export const CatalogProductSchema_ = z.object({
  _id: zExt.objectId(),
  name: z.string().min(1),
  description: z.string(),
  vendor: z.string().optional(),
  attributes: z.array(zExt.objectId()),
  externalId: z.string().nullish(),
  externalSource: z.string().nullish(),
  catalogId: zExt.objectId(),
  images: z.array(Product2ImageSchema),
  team: zExt.objectId(),
  variants: z.array(CatalogVariantSchema),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export const CatalogProductSchema: z.ZodType<CatalogProduct> =
  CatalogProductSchema_;
export interface CatalogProduct extends z.infer<typeof CatalogProductSchema_> {}

export const CatalogProductCreateSchema_ = CatalogProductSchema_.omit({
  _id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  variants: z.array(CatalogVariantCreateSchema),
  images: z.array(Product2ImageCreateSchema),
});
export const CatalogProductCreateSchema: z.ZodType<CatalogProductCreate> =
  CatalogProductCreateSchema_;
export type CatalogProductCreate = z.infer<typeof CatalogProductCreateSchema_>;

const CatalogProductUpdateSchema_ = CatalogProductSchema_.omit({
  createdAt: true,
  updatedAt: true,
});
export const CatalogProductUpdateSchema: z.ZodType<CatalogProductUpdate> =
  CatalogProductUpdateSchema_;
export type CatalogProductUpdate = z.infer<typeof CatalogProductUpdateSchema_>;

export const CatalogProductWithAttributesSchema_ = CatalogProductSchema_.extend(
  { attributes: z.array(Product2AttributeSchema) },
);
export const CatalogProductWithAttributesSchema: z.ZodType<CatalogProductWithAttributes> =
  CatalogProductWithAttributesSchema_;
export type CatalogProductWithAttributes = z.infer<
  typeof CatalogProductWithAttributesSchema_
>;

export const CatalogListingSchema_ = CatalogProductSchema_.extend({
  salesChannels: z.array(z.string()).default([]),
  status: createEnum(["active", "inactive", "draft", "archived"], {
    enumName: "CatalogListingStatus",
  }).default("active"),
  stockLevel: z.number().min(0).nullish(),
  committedStock: z.number().min(0).nullish(),
  prices: z
    .array(z.object({ amount: z.string(), currency: z.string() }))
    .default([]),
  skus: z.array(z.string()).default([]),
});
export const CatalogListingSchema: z.ZodType<CatalogListing> =
  CatalogListingSchema_;
export interface CatalogListing extends z.infer<typeof CatalogListingSchema_> {}
